import { useState } from 'react'
import {
  Users,
  Send,
  Plus,
  RefreshCw,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  UserPlus,
  Mail,
  Pencil,
  FileText,
  Upload,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { apiClient } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'

// Types
interface OutreachSegment {
  id: number
  name: string
  description: string | null
  template_id: number | null
  template_name: string | null
  custom_variables: Record<string, unknown>
  promo_code_id: number | null
  priority: number
  is_active: boolean
  user_count: number
  created_at: string
}

interface SegmentUser {
  uuid: string
  email: string
  name: string | null
  tier_name: string | null
  notes: string | null
  organization: string | null
  assigned_at: string | null
}

interface OutreachCampaign {
  id: number
  uuid: string
  name: string
  description: string | null
  status: string
  sender_email: string
  sender_name: string
  scheduled_at: string | null
  started_at: string | null
  completed_at: string | null
  total_segments: number
  total_recipients: number
  sent_count: number
  failed_count: number
  created_at: string
}

interface CampaignSegmentDetail {
  id: number
  segment_id: number
  segment_name: string
  priority: number
  template_name: string | null
  custom_variables: Record<string, unknown>
  status: string
  recipient_count: number
  sent_count: number
  failed_count: number
  email_send_batch_id: number | null
  error_message: string | null
  sent_at: string | null
}

interface EmailTemplate {
  id: number
  name: string
  subject: string
  body_html: string
  body_text: string | null
  description: string | null
  category: string
  variables_used: string[]
  is_active: boolean
  created_at: string
}

interface AvailableVariables {
  variables: Record<string, Record<string, string>>
  flat_list: string[]
}

interface PreviewResponse {
  subject: string
  body_html: string
  body_text: string | null
  user_email: string
  user_name: string | null
  segment_name: string
  template_name: string
  custom_variables: Record<string, unknown>
}

interface CampaignPreviewSegment {
  segment_id: number
  segment_name: string
  priority: number
  template_id: number | null
  template_name: string | null
  recipient_count: number
  sample_emails: string[]
  warnings: string[]
}

interface CampaignPreviewResponse {
  campaign_id: number
  campaign_name: string
  total_recipients: number
  unique_recipients: number
  segments: CampaignPreviewSegment[]
  warnings: string[]
  ready_to_send: boolean
}

// API functions
const fetchSegments = async (): Promise<OutreachSegment[]> => {
  const response = await apiClient.get('/admin/outreach/segments')
  return response.data
}

const fetchSegmentUsers = async (segmentId: number): Promise<SegmentUser[]> => {
  const response = await apiClient.get(`/admin/outreach/segments/${segmentId}/users`)
  return response.data
}

const fetchCampaigns = async (): Promise<OutreachCampaign[]> => {
  const response = await apiClient.get('/admin/outreach/campaigns')
  return response.data
}

const fetchCampaignSegments = async (campaignId: number): Promise<CampaignSegmentDetail[]> => {
  const response = await apiClient.get(`/admin/outreach/campaigns/${campaignId}/segments`)
  return response.data
}

const fetchTemplates = async (): Promise<EmailTemplate[]> => {
  const response = await apiClient.get('/admin/email-templates/templates')
  return response.data
}

const fetchVariables = async (): Promise<AvailableVariables> => {
  const response = await apiClient.get('/admin/email-templates/variables')
  return response.data
}

const createTemplate = async (data: {
  name: string
  subject: string
  body_html: string
  body_text?: string
  description?: string
  category: string
}): Promise<EmailTemplate> => {
  const response = await apiClient.post('/admin/email-templates/templates', data)
  return response.data
}

const updateTemplate = async ({
  templateId,
  data,
}: {
  templateId: number
  data: Partial<EmailTemplate>
}): Promise<EmailTemplate> => {
  const response = await apiClient.patch(`/admin/email-templates/templates/${templateId}`, data)
  return response.data
}

const deleteTemplate = async (templateId: number): Promise<void> => {
  await apiClient.delete(`/admin/email-templates/templates/${templateId}`)
}

const previewTemplate = async (
  templateId: number,
): Promise<{ subject: string; body_html: string; body_text: string | null }> => {
  const response = await apiClient.post(
    `/admin/email-templates/templates/${templateId}/preview`,
    {},
  )
  return response.data
}

const createSegment = async (data: {
  name: string
  description?: string
  template_id?: number
  custom_variables?: Record<string, unknown>
  priority?: number
}): Promise<OutreachSegment> => {
  const response = await apiClient.post('/admin/outreach/segments', data)
  return response.data
}

const updateSegment = async ({
  segmentId,
  data,
}: {
  segmentId: number
  data: Partial<OutreachSegment>
}): Promise<OutreachSegment> => {
  const response = await apiClient.patch(`/admin/outreach/segments/${segmentId}`, data)
  return response.data
}

const deleteSegment = async (segmentId: number): Promise<void> => {
  await apiClient.delete(`/admin/outreach/segments/${segmentId}`)
}

const assignUsersToSegment = async ({
  segmentId,
  emails,
}: {
  segmentId: number
  emails: string[]
}): Promise<{ assigned: number; already_assigned: number; not_found: string[] }> => {
  const response = await apiClient.post(`/admin/outreach/segments/${segmentId}/users`, { emails })
  return response.data
}

const removeUserFromSegment = async ({
  segmentId,
  userUuid,
}: {
  segmentId: number
  userUuid: string
}): Promise<void> => {
  await apiClient.delete(`/admin/outreach/segments/${segmentId}/users/${userUuid}`)
}

const createCampaign = async (data: {
  name: string
  description?: string
  segment_ids: number[]
  sender_email?: string
  sender_name?: string
}): Promise<OutreachCampaign> => {
  const response = await apiClient.post('/admin/outreach/campaigns', data)
  return response.data
}

const deleteCampaign = async (campaignId: number): Promise<void> => {
  await apiClient.delete(`/admin/outreach/campaigns/${campaignId}`)
}

const sendCampaign = async (campaignId: number): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(`/admin/outreach/campaigns/${campaignId}/send`)
  return response.data
}

const updateCampaign = async ({
  campaignId,
  data,
}: {
  campaignId: number
  data: {
    name?: string
    description?: string
    sender_email?: string
    sender_name?: string
    segment_ids?: number[]
  }
}): Promise<OutreachCampaign> => {
  const response = await apiClient.patch(`/admin/outreach/campaigns/${campaignId}`, data)
  return response.data
}

const previewSegmentEmail = async ({
  segmentId,
  userUuid,
}: {
  segmentId: number
  userUuid?: string
}): Promise<PreviewResponse> => {
  const response = await apiClient.post('/admin/outreach/preview', {
    segment_id: segmentId,
    user_uuid: userUuid,
  })
  return response.data
}

const bulkImportUsers = async ({
  segmentId,
  file,
}: {
  segmentId: number
  file: File
}): Promise<{
  total_rows: number
  assigned: number
  already_assigned: number
  not_found: string[]
  errors: string[]
}> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post(
    `/admin/outreach/segments/${segmentId}/users/bulk-import`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  )
  return response.data
}

const previewCampaign = async (campaignId: number): Promise<CampaignPreviewResponse> => {
  const response = await apiClient.post(`/admin/outreach/campaigns/${campaignId}/preview`)
  return response.data
}

const cancelCampaign = async ({
  campaignId,
  reason,
}: {
  campaignId: number
  reason?: string
}): Promise<{
  success: boolean
  message: string
  warning?: string
}> => {
  const response = await apiClient.post(`/admin/outreach/campaigns/${campaignId}/cancel`, null, {
    params: reason ? { reason } : undefined,
  })
  return response.data
}

// Helper functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'draft':
      return (
        <Badge variant="secondary">
          <Clock className="h-3 w-3 mr-1" />
          Draft
        </Badge>
      )
    case 'scheduled':
      return (
        <Badge variant="default" className="bg-blue-500">
          <Clock className="h-3 w-3 mr-1" />
          Scheduled
        </Badge>
      )
    case 'sending':
      return (
        <Badge variant="default" className="bg-yellow-500">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          Sending
        </Badge>
      )
    case 'sent':
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle className="h-3 w-3 mr-1" />
          Sent
        </Badge>
      )
    case 'cancelled':
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          Cancelled
        </Badge>
      )
    case 'failed':
      return (
        <Badge variant="destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      )
    case 'pending':
      return (
        <Badge variant="outline">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      )
    default:
      return <Badge>{status}</Badge>
  }
}

export function OutreachTab() {
  const queryClient = useQueryClient()
  const [activeSubTab, setActiveSubTab] = useState<'templates' | 'segments' | 'campaigns'>(
    'templates',
  )

  // Template state
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState('')
  const [templatePreviewSubject, setTemplatePreviewSubject] = useState('')
  const [showTemplatePreview, setShowTemplatePreview] = useState(false)
  const [selectedVarCategory, setSelectedVarCategory] = useState<string>('all')

  // Create template state
  const [newTemplateName, setNewTemplateName] = useState('')
  const [newTemplateSubject, setNewTemplateSubject] = useState('')
  const [newTemplateBodyHtml, setNewTemplateBodyHtml] = useState('')
  const [newTemplateBodyText, setNewTemplateBodyText] = useState('')
  const [newTemplateDescription, setNewTemplateDescription] = useState('')
  const [newTemplateCategory, setNewTemplateCategory] = useState('promotion')

  // Segment state
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null)
  const [showSegmentUsers, setShowSegmentUsers] = useState(false)
  const [showAddUsers, setShowAddUsers] = useState(false)
  const [addUsersEmails, setAddUsersEmails] = useState('')
  const [editingSegment, setEditingSegment] = useState<OutreachSegment | null>(null)

  // Create segment state
  const [newSegmentName, setNewSegmentName] = useState('')
  const [newSegmentDesc, setNewSegmentDesc] = useState('')
  const [newSegmentTemplateId, setNewSegmentTemplateId] = useState<number | null>(null)
  const [newSegmentPriority, setNewSegmentPriority] = useState(0)
  const [newSegmentVars, setNewSegmentVars] = useState('')

  // Campaign state
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [showCampaignDetails, setShowCampaignDetails] = useState(false)
  const [confirmSendCampaign, setConfirmSendCampaign] = useState<OutreachCampaign | null>(null)

  // Create campaign state
  const [newCampaignName, setNewCampaignName] = useState('')
  const [newCampaignDesc, setNewCampaignDesc] = useState('')
  const [newCampaignSegments, setNewCampaignSegments] = useState<number[]>([])
  const [newCampaignSenderEmail, setNewCampaignSenderEmail] = useState('info@ecosemantic.com')
  const [newCampaignSenderName, setNewCampaignSenderName] = useState('Carlos at EcoSemantic')

  // Edit campaign state
  const [editingCampaign, setEditingCampaign] = useState<OutreachCampaign | null>(null)
  const [editCampaignSegments, setEditCampaignSegments] = useState<number[]>([])

  // Preview state
  const [showPreview, setShowPreview] = useState(false)
  const [previewSegmentId, setPreviewSegmentId] = useState<number | null>(null)
  const [previewUserUuid, setPreviewUserUuid] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null)
  const [previewUsersForSegment, setPreviewUsersForSegment] = useState<SegmentUser[]>([])

  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null)

  // Campaign preview state
  const [showCampaignPreview, setShowCampaignPreview] = useState(false)
  const [campaignPreviewData, setCampaignPreviewData] = useState<CampaignPreviewResponse | null>(
    null,
  )

  // Campaign cancel state
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelCampaignId, setCancelCampaignId] = useState<number | null>(null)
  const [cancelReason, setCancelReason] = useState('')

  // Queries
  const { data: segments = [], isLoading: segmentsLoading } = useQuery({
    queryKey: ['outreach-segments'],
    queryFn: fetchSegments,
  })

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['outreach-campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: (query) => {
      const data = query.state.data as OutreachCampaign[] | undefined
      return data?.some((c) => c.status === 'sending') ? 5000 : false
    },
  })

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: fetchTemplates,
  })

  const { data: variables } = useQuery({
    queryKey: ['email-variables'],
    queryFn: fetchVariables,
  })

  const { data: segmentUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['segment-users', selectedSegmentId],
    queryFn: () => fetchSegmentUsers(selectedSegmentId!),
    enabled: !!selectedSegmentId && showSegmentUsers,
  })

  const { data: campaignSegments = [] } = useQuery({
    queryKey: ['campaign-segments', selectedCampaignId],
    queryFn: () => fetchCampaignSegments(selectedCampaignId!),
    enabled: !!selectedCampaignId && showCampaignDetails,
  })

  // Mutations
  const createTemplateMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: (data) => {
      toast.success(`Template "${data.name}" created`)
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      resetTemplateForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create template')),
  })

  const updateTemplateMutation = useMutation({
    mutationFn: updateTemplate,
    onSuccess: (data) => {
      toast.success(`Template "${data.name}" updated`)
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setEditingTemplate(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update template')),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      toast.success('Template deleted')
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to delete template')),
  })

  const previewTemplateMutation = useMutation({
    mutationFn: previewTemplate,
    onSuccess: (data) => {
      setTemplatePreviewSubject(data.subject)
      setTemplatePreviewHtml(data.body_html)
      setShowTemplatePreview(true)
    },
    onError: () => toast.error('Failed to preview template'),
  })

  const createSegmentMutation = useMutation({
    mutationFn: createSegment,
    onSuccess: (data) => {
      toast.success(`Segment "${data.name}" created`)
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
      resetSegmentForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create segment')),
  })

  const updateSegmentMutation = useMutation({
    mutationFn: updateSegment,
    onSuccess: (data) => {
      toast.success(`Segment "${data.name}" updated`)
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
      setEditingSegment(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update segment')),
  })

  const deleteSegmentMutation = useMutation({
    mutationFn: deleteSegment,
    onSuccess: () => {
      toast.success('Segment deleted')
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to delete segment')),
  })

  const assignUsersMutation = useMutation({
    mutationFn: assignUsersToSegment,
    onSuccess: (data) => {
      toast.success(
        `Assigned ${data.assigned} users${data.not_found.length > 0 ? `, ${data.not_found.length} not found` : ''}`,
      )
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
      queryClient.invalidateQueries({ queryKey: ['segment-users', selectedSegmentId] })
      setShowAddUsers(false)
      setAddUsersEmails('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to assign users')),
  })

  const removeUserMutation = useMutation({
    mutationFn: removeUserFromSegment,
    onSuccess: () => {
      toast.success('User removed from segment')
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
      queryClient.invalidateQueries({ queryKey: ['segment-users', selectedSegmentId] })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to remove user')),
  })

  const createCampaignMutation = useMutation({
    mutationFn: createCampaign,
    onSuccess: (data) => {
      toast.success(`Campaign "${data.name}" created`)
      queryClient.invalidateQueries({ queryKey: ['outreach-campaigns'] })
      resetCampaignForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create campaign')),
  })

  const deleteCampaignMutation = useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      toast.success('Campaign deleted')
      queryClient.invalidateQueries({ queryKey: ['outreach-campaigns'] })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to delete campaign')),
  })

  const sendCampaignMutation = useMutation({
    mutationFn: sendCampaign,
    onSuccess: () => {
      toast.success('Campaign is being sent!')
      queryClient.invalidateQueries({ queryKey: ['outreach-campaigns'] })
      setConfirmSendCampaign(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to send campaign')),
  })

  const updateCampaignMutation = useMutation({
    mutationFn: updateCampaign,
    onSuccess: (data) => {
      toast.success(`Campaign "${data.name}" updated`)
      queryClient.invalidateQueries({ queryKey: ['outreach-campaigns'] })
      setEditingCampaign(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update campaign')),
  })

  const previewMutation = useMutation({
    mutationFn: previewSegmentEmail,
    onSuccess: (data) => {
      setPreviewData(data)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to generate preview')),
  })

  const bulkImportMutation = useMutation({
    mutationFn: bulkImportUsers,
    onSuccess: (data) => {
      const msg = `Imported ${data.total_rows} rows: ${data.assigned} assigned, ${data.already_assigned} already assigned`
      if (data.not_found.length > 0) {
        toast.warning(`${msg}. ${data.not_found.length} emails not found`)
      } else {
        toast.success(msg)
      }
      if (data.errors.length > 0) {
        toast.error(`${data.errors.length} parsing errors occurred`)
      }
      queryClient.invalidateQueries({ queryKey: ['outreach-segments'] })
      setShowBulkImport(false)
      setBulkImportFile(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to import users')),
  })

  const campaignPreviewMutation = useMutation({
    mutationFn: previewCampaign,
    onSuccess: (data) => {
      setCampaignPreviewData(data)
      setShowCampaignPreview(true)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to preview campaign')),
  })

  const cancelCampaignMutation = useMutation({
    mutationFn: cancelCampaign,
    onSuccess: (data) => {
      toast.success(data.message)
      if (data.warning) toast.warning(data.warning)
      queryClient.invalidateQueries({ queryKey: ['outreach-campaigns'] })
      setShowCancelDialog(false)
      setCancelCampaignId(null)
      setCancelReason('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to cancel campaign')),
  })

  const resetTemplateForm = () => {
    setNewTemplateName('')
    setNewTemplateSubject('')
    setNewTemplateBodyHtml('')
    setNewTemplateBodyText('')
    setNewTemplateDescription('')
    setNewTemplateCategory('promotion')
  }

  const resetSegmentForm = () => {
    setNewSegmentName('')
    setNewSegmentDesc('')
    setNewSegmentTemplateId(null)
    setNewSegmentPriority(0)
    setNewSegmentVars('')
  }

  const resetCampaignForm = () => {
    setNewCampaignName('')
    setNewCampaignDesc('')
    setNewCampaignSegments([])
    setNewCampaignSenderEmail('info@ecosemantic.com')
    setNewCampaignSenderName('Carlos at EcoSemantic')
  }

  const handleCreateTemplate = () => {
    if (!newTemplateName || !newTemplateSubject || !newTemplateBodyHtml) {
      toast.error('Name, subject, and HTML body are required')
      return
    }
    createTemplateMutation.mutate({
      name: newTemplateName,
      subject: newTemplateSubject,
      body_html: newTemplateBodyHtml,
      body_text: newTemplateBodyText || undefined,
      description: newTemplateDescription || undefined,
      category: newTemplateCategory,
    })
  }

  const getFilteredVariables = () => {
    if (!variables?.variables) return []
    if (selectedVarCategory === 'all') {
      return Object.entries(variables.variables).flatMap(([cat, vars]) =>
        Object.entries(vars).map(([name, desc]) => ({
          category: cat,
          name,
          desc,
          full: `${cat}.${name}`,
        })),
      )
    }
    const catVars = variables.variables[selectedVarCategory] || {}
    return Object.entries(catVars).map(([name, desc]) => ({
      category: selectedVarCategory,
      name,
      desc,
      full: `${selectedVarCategory}.${name}`,
    }))
  }

  const handleCreateSegment = () => {
    if (!newSegmentName) {
      toast.error('Segment name is required')
      return
    }
    let customVars: Record<string, unknown> = {}
    if (newSegmentVars.trim()) {
      try {
        customVars = JSON.parse(newSegmentVars)
      } catch {
        toast.error('Invalid JSON for custom variables')
        return
      }
    }
    createSegmentMutation.mutate({
      name: newSegmentName,
      description: newSegmentDesc || undefined,
      template_id: newSegmentTemplateId || undefined,
      custom_variables: customVars,
      priority: newSegmentPriority,
    })
  }

  const handleCreateCampaign = () => {
    if (!newCampaignName || newCampaignSegments.length === 0) {
      toast.error('Campaign name and at least one segment are required')
      return
    }
    createCampaignMutation.mutate({
      name: newCampaignName,
      description: newCampaignDesc || undefined,
      segment_ids: newCampaignSegments,
      sender_email: newCampaignSenderEmail,
      sender_name: newCampaignSenderName,
    })
  }

  const handleAddUsers = () => {
    if (!selectedSegmentId || !addUsersEmails.trim()) return
    const emails = addUsersEmails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
    if (emails.length === 0) {
      toast.error('Please enter at least one email')
      return
    }
    assignUsersMutation.mutate({ segmentId: selectedSegmentId, emails })
  }

  const toggleSegmentSelection = (segmentId: number) => {
    setNewCampaignSegments((prev) =>
      prev.includes(segmentId) ? prev.filter((id) => id !== segmentId) : [...prev, segmentId],
    )
  }

  const toggleEditSegmentSelection = (segmentId: number) => {
    setEditCampaignSegments((prev) =>
      prev.includes(segmentId) ? prev.filter((id) => id !== segmentId) : [...prev, segmentId],
    )
  }

  const handleOpenEditCampaign = async (campaign: OutreachCampaign) => {
    setEditingCampaign(campaign)
    // Fetch current segments for this campaign
    try {
      const segments = await fetchCampaignSegments(campaign.id)
      setEditCampaignSegments(segments.map((s) => s.segment_id))
    } catch {
      setEditCampaignSegments([])
    }
  }

  const handleOpenPreview = async (segmentId: number) => {
    setPreviewSegmentId(segmentId)
    setPreviewUserUuid(null)
    setPreviewData(null)
    setShowPreview(true)
    // Fetch users for segment
    try {
      const users = await fetchSegmentUsers(segmentId)
      setPreviewUsersForSegment(users)
      // Auto-load preview for first user
      if (users.length > 0) {
        previewMutation.mutate({ segmentId, userUuid: users[0].uuid })
        setPreviewUserUuid(users[0].uuid)
      }
    } catch {
      setPreviewUsersForSegment([])
    }
  }

  const handlePreviewUserChange = (userUuid: string) => {
    setPreviewUserUuid(userUuid)
    if (previewSegmentId) {
      previewMutation.mutate({ segmentId: previewSegmentId, userUuid })
    }
  }

  const handlePreviewSegmentChange = async (segmentId: number) => {
    setPreviewSegmentId(segmentId)
    setPreviewUserUuid(null)
    setPreviewData(null)
    try {
      const users = await fetchSegmentUsers(segmentId)
      setPreviewUsersForSegment(users)
      if (users.length > 0) {
        previewMutation.mutate({ segmentId, userUuid: users[0].uuid })
        setPreviewUserUuid(users[0].uuid)
      }
    } catch {
      setPreviewUsersForSegment([])
    }
  }

  return (
    <div className="space-y-6">
      <Tabs
        value={activeSubTab}
        onValueChange={(v) => setActiveSubTab(v as 'templates' | 'segments' | 'campaigns')}
      >
        <TabsList>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="segments" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Segments
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Campaigns
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Email Templates ({templates.length})
              </CardTitle>
              <CardDescription>Reusable email templates with Jinja2 variables</CardDescription>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No templates yet. Create your first one below.
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Variables</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-mono text-xs">{t.id}</TableCell>
                          <TableCell className="font-medium">{t.name}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{t.subject}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{t.category}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {t.variables_used?.length || 0} vars
                            </span>
                          </TableCell>
                          <TableCell className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => previewTemplateMutation.mutate(t.id)}
                              disabled={previewTemplateMutation.isPending}
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTemplate(t)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteTemplateMutation.mutate(t.id)}
                              disabled={deleteTemplateMutation.isPending}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available Variables */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Available Template Variables
              </CardTitle>
              <CardDescription>Use these in templates with {'{{variable}}'} syntax</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <Select value={selectedVarCategory} onValueChange={setSelectedVarCategory}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {variables?.variables &&
                      Object.keys(variables.variables).map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {getFilteredVariables().map((v) => (
                  <div
                    key={v.full}
                    className="flex items-start gap-2 p-2 rounded border bg-muted/30"
                  >
                    <code className="text-xs font-mono bg-primary/10 px-1.5 py-0.5 rounded whitespace-nowrap">{`{{${v.full}}}`}</code>
                    <span className="text-xs text-muted-foreground">{v.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Create Template Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Create Template
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Template Name *</Label>
                  <Input
                    placeholder="e.g., January 2026 Outreach"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newTemplateCategory} onValueChange={setNewTemplateCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="welcome">Welcome</SelectItem>
                      <SelectItem value="feedback">Feedback</SelectItem>
                      <SelectItem value="promotion">Promotion</SelectItem>
                      <SelectItem value="announcement">Announcement</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Subject *</Label>
                <Input
                  placeholder="Hi {{user.name}}, we have news!"
                  value={newTemplateSubject}
                  onChange={(e) => setNewTemplateSubject(e.target.value)}
                />
              </div>
              <div>
                <Label>HTML Body *</Label>
                <Textarea
                  rows={10}
                  placeholder="<h1>Hello {{user.name}}!</h1>"
                  value={newTemplateBodyHtml}
                  onChange={(e) => setNewTemplateBodyHtml(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label>Plain Text Body (optional)</Label>
                <Textarea
                  rows={4}
                  placeholder="Hello {{user.name}}!"
                  value={newTemplateBodyText}
                  onChange={(e) => setNewTemplateBodyText(e.target.value)}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  placeholder="Internal description"
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                />
              </div>
              <Button
                onClick={handleCreateTemplate}
                disabled={
                  createTemplateMutation.isPending ||
                  !newTemplateName ||
                  !newTemplateSubject ||
                  !newTemplateBodyHtml
                }
              >
                {createTemplateMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create Template
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Outreach Segments ({segments.length})
              </CardTitle>
              <CardDescription>Manage user segments for targeted email campaigns</CardDescription>
            </CardHeader>
            <CardContent>
              {segmentsLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : segments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No segments yet. Create your first one below.
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Template</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {segments.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="font-mono text-xs">{s.id}</TableCell>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell className="text-sm">
                            {s.template_name || <span className="text-muted-foreground">None</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{s.user_count} users</Badge>
                          </TableCell>
                          <TableCell>{s.priority}</TableCell>
                          <TableCell>
                            {s.is_active ? (
                              <Badge variant="default" className="bg-green-500">
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenPreview(s.id)}
                              title="Preview email"
                              disabled={!s.template_id}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSegmentId(s.id)
                                setShowSegmentUsers(true)
                              }}
                              title="View users"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSegmentId(s.id)
                                setShowAddUsers(true)
                              }}
                              title="Add users"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSegmentId(s.id)
                                setShowBulkImport(true)
                              }}
                              title="Bulk import from CSV"
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingSegment(s)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSegmentMutation.mutate(s.id)}
                              disabled={deleteSegmentMutation.isPending}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Segment Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Create Segment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Segment Name *</Label>
                  <Input
                    placeholder="e.g., VIP Users"
                    value={newSegmentName}
                    onChange={(e) => setNewSegmentName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Template</Label>
                  <Select
                    value={newSegmentTemplateId?.toString() || ''}
                    onValueChange={(v) => setNewSegmentTemplateId(v ? parseInt(v) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id.toString()}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Description of this segment"
                  value={newSegmentDesc}
                  onChange={(e) => setNewSegmentDesc(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority (lower = sent first)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newSegmentPriority}
                    onChange={(e) => setNewSegmentPriority(parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div>
                <Label>Custom Variables (JSON)</Label>
                <Textarea
                  rows={3}
                  className="font-mono text-sm"
                  placeholder='{"subject": "Hello!", "promo_code": "SAVE20"}'
                  value={newSegmentVars}
                  onChange={(e) => setNewSegmentVars(e.target.value)}
                />
              </div>
              <Button
                onClick={handleCreateSegment}
                disabled={createSegmentMutation.isPending || !newSegmentName}
              >
                {createSegmentMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create Segment
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Outreach Campaigns ({campaigns.length})
              </CardTitle>
              <CardDescription>Manage bulk email campaigns across segments</CardDescription>
            </CardHeader>
            <CardContent>
              {campaignsLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No campaigns yet. Create your first one below.
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Segments</TableHead>
                        <TableHead>Recipients</TableHead>
                        <TableHead>Sent/Failed</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaigns.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-xs">{c.id}</TableCell>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>{getStatusBadge(c.status)}</TableCell>
                          <TableCell>{c.total_segments}</TableCell>
                          <TableCell>{c.total_recipients}</TableCell>
                          <TableCell>
                            <span className="text-green-600">{c.sent_count}</span> /{' '}
                            <span className="text-red-600">{c.failed_count}</span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(c.created_at)}
                          </TableCell>
                          <TableCell className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCampaignId(c.id)
                                setShowCampaignDetails(true)
                              }}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {c.status === 'draft' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => campaignPreviewMutation.mutate(c.id)}
                                  disabled={campaignPreviewMutation.isPending}
                                  title="Preview campaign"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Preview
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEditCampaign(c)}
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => setConfirmSendCampaign(c)}
                                  disabled={sendCampaignMutation.isPending}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Send
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteCampaignMutation.mutate(c.id)}
                                  disabled={deleteCampaignMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </>
                            )}
                            {(c.status === 'scheduled' || c.status === 'sending') && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setCancelCampaignId(c.id)
                                  setShowCancelDialog(true)
                                }}
                                title="Cancel campaign"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Campaign Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Create Campaign
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Campaign Name *</Label>
                  <Input
                    placeholder="e.g., January 2026 Outreach"
                    value={newCampaignName}
                    onChange={(e) => setNewCampaignName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    placeholder="Campaign description"
                    value={newCampaignDesc}
                    onChange={(e) => setNewCampaignDesc(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sender Email</Label>
                  <Input
                    placeholder="info@ecosemantic.com"
                    value={newCampaignSenderEmail}
                    onChange={(e) => setNewCampaignSenderEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Sender Name</Label>
                  <Input
                    placeholder="Carlos at EcoSemantic"
                    value={newCampaignSenderName}
                    onChange={(e) => setNewCampaignSenderName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Select Segments *</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Click to select/deselect segments for this campaign
                </p>
                <div className="flex flex-wrap gap-2">
                  {segments
                    .filter((s) => s.is_active && s.template_id)
                    .map((s) => (
                      <Badge
                        key={s.id}
                        variant={newCampaignSegments.includes(s.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleSegmentSelection(s.id)}
                      >
                        {s.name} ({s.user_count})
                      </Badge>
                    ))}
                </div>
                {newCampaignSegments.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {newCampaignSegments.length} segment(s) selected
                  </p>
                )}
              </div>
              <Button
                onClick={handleCreateCampaign}
                disabled={
                  createCampaignMutation.isPending ||
                  !newCampaignName ||
                  newCampaignSegments.length === 0
                }
              >
                {createCampaignMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Preview Dialog */}
      <Dialog open={showTemplatePreview} onOpenChange={setShowTemplatePreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>Subject: {templatePreviewSubject}</DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg p-4 bg-white text-black">
            <div dangerouslySetInnerHTML={{ __html: templatePreviewHtml }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>Update the template details</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, subject: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={editingTemplate.category}
                  onValueChange={(v) => setEditingTemplate({ ...editingTemplate, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="welcome">Welcome</SelectItem>
                    <SelectItem value="feedback">Feedback</SelectItem>
                    <SelectItem value="promotion">Promotion</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={editingTemplate.description || ''}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, description: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>HTML Body</Label>
                <Textarea
                  rows={10}
                  className="font-mono text-sm"
                  value={editingTemplate.body_html}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, body_html: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Plain Text Body (optional)</Label>
                <Textarea
                  rows={4}
                  className="font-mono text-sm"
                  value={editingTemplate.body_text || ''}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, body_text: e.target.value })
                  }
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    updateTemplateMutation.mutate({
                      templateId: editingTemplate.id,
                      data: editingTemplate,
                    })
                  }
                  disabled={updateTemplateMutation.isPending}
                >
                  {updateTemplateMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Pencil className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Segment Users Dialog */}
      <Dialog
        open={showSegmentUsers}
        onOpenChange={(open) => {
          if (!open) {
            setShowSegmentUsers(false)
            setSelectedSegmentId(null)
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Segment Users</DialogTitle>
            <DialogDescription>
              {segments.find((s) => s.id === selectedSegmentId)?.name} - {segmentUsers.length} users
            </DialogDescription>
          </DialogHeader>
          {usersLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : segmentUsers.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No users in this segment</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {segmentUsers.map((u) => (
                    <TableRow key={u.uuid}>
                      <TableCell className="font-mono text-sm">{u.email}</TableCell>
                      <TableCell>{u.name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{u.tier_name || 'Free'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            removeUserMutation.mutate({
                              segmentId: selectedSegmentId!,
                              userUuid: u.uuid,
                            })
                          }
                          disabled={removeUserMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Users Dialog */}
      <Dialog
        open={showAddUsers}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddUsers(false)
            setAddUsersEmails('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Users to Segment</DialogTitle>
            <DialogDescription>
              Add users to: {segments.find((s) => s.id === selectedSegmentId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Addresses</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Enter emails separated by commas, semicolons, or new lines
              </p>
              <Textarea
                rows={6}
                placeholder="user1@example.com&#10;user2@example.com"
                value={addUsersEmails}
                onChange={(e) => setAddUsersEmails(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddUsers(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddUsers}
                disabled={assignUsersMutation.isPending || !addUsersEmails.trim()}
              >
                {assignUsersMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                Add Users
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Segment Dialog */}
      <Dialog open={!!editingSegment} onOpenChange={(open) => !open && setEditingSegment(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Segment</DialogTitle>
          </DialogHeader>
          {editingSegment && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingSegment.name}
                  onChange={(e) => setEditingSegment({ ...editingSegment, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={editingSegment.description || ''}
                  onChange={(e) =>
                    setEditingSegment({ ...editingSegment, description: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Template</Label>
                <Select
                  value={editingSegment.template_id?.toString() || ''}
                  onValueChange={(v) =>
                    setEditingSegment({ ...editingSegment, template_id: v ? parseInt(v) : null })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id.toString()}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  min={0}
                  value={editingSegment.priority}
                  onChange={(e) =>
                    setEditingSegment({
                      ...editingSegment,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <Label>Custom Variables (JSON)</Label>
                <Textarea
                  rows={4}
                  className="font-mono text-sm"
                  value={JSON.stringify(editingSegment.custom_variables, null, 2)}
                  onChange={(e) => {
                    try {
                      setEditingSegment({
                        ...editingSegment,
                        custom_variables: JSON.parse(e.target.value),
                      })
                    } catch {
                      // Ignore parse errors while the user is mid-edit; the
                      // textarea keeps the raw text and only valid JSON commits.
                    }
                  }}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingSegment(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    updateSegmentMutation.mutate({
                      segmentId: editingSegment.id,
                      data: editingSegment,
                    })
                  }
                  disabled={updateSegmentMutation.isPending}
                >
                  {updateSegmentMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Pencil className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog
        open={!!editingCampaign}
        onOpenChange={(open) => {
          if (!open) {
            setEditingCampaign(null)
            setEditCampaignSegments([])
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>
              Edit campaign settings (only draft campaigns can be edited)
            </DialogDescription>
          </DialogHeader>
          {editingCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Campaign Name</Label>
                  <Input
                    value={editingCampaign.name}
                    onChange={(e) =>
                      setEditingCampaign({ ...editingCampaign, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editingCampaign.description || ''}
                    onChange={(e) =>
                      setEditingCampaign({ ...editingCampaign, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sender Email</Label>
                  <Input
                    value={editingCampaign.sender_email}
                    onChange={(e) =>
                      setEditingCampaign({ ...editingCampaign, sender_email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Sender Name</Label>
                  <Input
                    value={editingCampaign.sender_name}
                    onChange={(e) =>
                      setEditingCampaign({ ...editingCampaign, sender_name: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Segments</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Click to add/remove segments from this campaign
                </p>
                <div className="flex flex-wrap gap-2">
                  {segments
                    .filter((s) => s.is_active && s.template_id)
                    .map((s) => (
                      <Badge
                        key={s.id}
                        variant={editCampaignSegments.includes(s.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleEditSegmentSelection(s.id)}
                      >
                        {s.name} ({s.user_count})
                      </Badge>
                    ))}
                </div>
                {editCampaignSegments.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {editCampaignSegments.length} segment(s) selected
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingCampaign(null)
                    setEditCampaignSegments([])
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    updateCampaignMutation.mutate({
                      campaignId: editingCampaign.id,
                      data: {
                        name: editingCampaign.name,
                        description: editingCampaign.description,
                        sender_email: editingCampaign.sender_email,
                        sender_name: editingCampaign.sender_name,
                        segment_ids: editCampaignSegments,
                      },
                    })
                  }
                  disabled={updateCampaignMutation.isPending || editCampaignSegments.length === 0}
                >
                  {updateCampaignMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Pencil className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog
        open={showCampaignDetails}
        onOpenChange={(open) => {
          if (!open) {
            setShowCampaignDetails(false)
            setSelectedCampaignId(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Details</DialogTitle>
            <DialogDescription>
              {campaigns.find((c) => c.id === selectedCampaignId)?.name}
            </DialogDescription>
          </DialogHeader>
          {campaignSegments.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Loading segment details...</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Segment</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Sent/Failed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignSegments.map((cs) => (
                    <TableRow key={cs.id}>
                      <TableCell className="font-medium">{cs.segment_name}</TableCell>
                      <TableCell className="text-sm">{cs.template_name || '-'}</TableCell>
                      <TableCell>{getStatusBadge(cs.status)}</TableCell>
                      <TableCell>{cs.recipient_count}</TableCell>
                      <TableCell>
                        <span className="text-green-600">{cs.sent_count}</span> /{' '}
                        <span className="text-red-600">{cs.failed_count}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Send Campaign Dialog */}
      <AlertDialog
        open={!!confirmSendCampaign}
        onOpenChange={(open) => !open && setConfirmSendCampaign(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send "{confirmSendCampaign?.name}"? This will send emails to
              all recipients in the selected segments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmSendCampaign && sendCampaignMutation.mutate(confirmSendCampaign.id)
              }
              disabled={sendCampaignMutation.isPending}
            >
              {sendCampaignMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Send Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Preview Dialog */}
      <Dialog
        open={showPreview}
        onOpenChange={(open) => {
          if (!open) {
            setShowPreview(false)
            setPreviewData(null)
            setPreviewSegmentId(null)
            setPreviewUserUuid(null)
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview how the email will look for a specific user in a segment
            </DialogDescription>
          </DialogHeader>

          {/* Filters */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Segment</Label>
              <Select
                value={previewSegmentId?.toString() || ''}
                onValueChange={(v) => handlePreviewSegmentChange(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select segment" />
                </SelectTrigger>
                <SelectContent>
                  {segments
                    .filter((s) => s.template_id)
                    .map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>
                        {s.name} ({s.user_count} users)
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>User</Label>
              {previewUsersForSegment.length === 0 ? (
                <div className="h-9 px-3 py-2 border rounded-md text-sm text-muted-foreground">
                  No users in segment
                </div>
              ) : (
                <Select value={previewUserUuid || ''} onValueChange={handlePreviewUserChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {previewUsersForSegment.map((u) => (
                      <SelectItem key={u.uuid} value={u.uuid}>
                        {u.email} {u.name && `(${u.name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Preview info */}
          {previewData && (
            <div className="mb-4 p-3 bg-muted rounded-lg space-y-1">
              <p className="text-sm">
                <strong>Template:</strong> {previewData.template_name}
              </p>
              <p className="text-sm">
                <strong>Recipient:</strong> {previewData.user_email}{' '}
                {previewData.user_name && `(${previewData.user_name})`}
              </p>
              {Object.keys(previewData.custom_variables).length > 0 && (
                <p className="text-sm">
                  <strong>Custom Variables:</strong> {JSON.stringify(previewData.custom_variables)}
                </p>
              )}
            </div>
          )}

          {/* Loading state */}
          {previewMutation.isPending && (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          )}

          {/* Preview content */}
          {previewData && !previewMutation.isPending && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Subject:</p>
                <p className="font-medium">{previewData.subject}</p>
              </div>
              <div className="border rounded-lg p-4 bg-white">
                <div dangerouslySetInnerHTML={{ __html: previewData.body_html }} />
              </div>
            </div>
          )}

          {/* No preview state */}
          {!previewData &&
            !previewMutation.isPending &&
            previewSegmentId &&
            previewUsersForSegment.length === 0 && (
              <p className="text-center py-8 text-muted-foreground">
                This segment has no users to preview
              </p>
            )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import CSV Dialog */}
      <Dialog
        open={showBulkImport}
        onOpenChange={(open) => {
          if (!open) {
            setShowBulkImport(false)
            setBulkImportFile(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Import Users (CSV)</DialogTitle>
            <DialogDescription>
              Upload CSV to add users to: {segments.find((s) => s.id === selectedSegmentId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setBulkImportFile(e.target.files?.[0] || null)}
              />
              {bulkImportFile && (
                <p className="text-sm text-muted-foreground mt-1">
                  Selected: {bulkImportFile.name} ({(bulkImportFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">CSV Format (header required):</p>
              <code className="block bg-muted p-2 rounded text-xs">
                email,name,notes,organization
                <br />
                user@example.com,John Doe,VIP,Acme Corp
              </code>
              <p className="mt-2 text-xs">Only the email column is required.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkImport(false)}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  bulkImportFile &&
                  selectedSegmentId &&
                  bulkImportMutation.mutate({ segmentId: selectedSegmentId, file: bulkImportFile })
                }
                disabled={!bulkImportFile || bulkImportMutation.isPending}
              >
                {bulkImportMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Import
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Preview Dialog */}
      <Dialog
        open={showCampaignPreview}
        onOpenChange={(open) => {
          if (!open) {
            setShowCampaignPreview(false)
            setCampaignPreviewData(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Preview: {campaignPreviewData?.campaign_name}</DialogTitle>
            <DialogDescription>Review campaign before sending</DialogDescription>
          </DialogHeader>

          {campaignPreviewData && (
            <div className="space-y-4">
              {/* Overview */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Recipients</p>
                      <p className="text-2xl font-bold">{campaignPreviewData.total_recipients}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Unique Recipients</p>
                      <p className="text-2xl font-bold">{campaignPreviewData.unique_recipients}</p>
                    </div>
                  </div>

                  {/* Ready to send indicator */}
                  <div className="mt-4">
                    {campaignPreviewData.ready_to_send ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Ready to Send
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Not Ready
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Warnings */}
              {campaignPreviewData.warnings.length > 0 && (
                <Card className="border-yellow-500">
                  <CardHeader>
                    <CardTitle className="text-yellow-600 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Warnings ({campaignPreviewData.warnings.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-disc pl-5 space-y-1">
                      {campaignPreviewData.warnings.map((w, i) => (
                        <li key={i} className="text-sm">
                          {w}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Segment breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Segment Breakdown ({campaignPreviewData.segments.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {campaignPreviewData.segments.map((seg) => (
                      <div key={seg.segment_id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{seg.segment_name}</p>
                            <p className="text-sm text-muted-foreground">
                              Template: {seg.template_name || 'None'}
                            </p>
                          </div>
                          <Badge variant="outline">{seg.recipient_count} users</Badge>
                        </div>

                        {/* Sample emails */}
                        {seg.sample_emails.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground mb-1">Sample recipients:</p>
                            <div className="flex flex-wrap gap-1">
                              {seg.sample_emails.map((email, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {email}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Segment warnings */}
                        {seg.warnings.length > 0 && (
                          <div className="mt-2">
                            {seg.warnings.map((w, i) => (
                              <p
                                key={i}
                                className="text-xs text-yellow-600 flex items-center gap-1"
                              >
                                <AlertCircle className="h-3 w-3" />
                                {w}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCampaignPreview(false)}>
                  Close
                </Button>
                {campaignPreviewData.ready_to_send && (
                  <Button
                    onClick={() => {
                      setShowCampaignPreview(false)
                      const campaign = campaigns.find(
                        (c) => c.id === campaignPreviewData.campaign_id,
                      )
                      if (campaign) setConfirmSendCampaign(campaign)
                    }}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Proceed to Send
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Campaign Dialog */}
      <AlertDialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCancelDialog(false)
            setCancelCampaignId(null)
            setCancelReason('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Campaign?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to cancel this campaign?</p>
              <p className="text-xs text-yellow-600">⚠️ Emails already queued may still be sent</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Reason (optional)</Label>
            <Textarea
              placeholder="Why are you cancelling this campaign?"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Campaign</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() =>
                cancelCampaignId &&
                cancelCampaignMutation.mutate({
                  campaignId: cancelCampaignId,
                  reason: cancelReason || undefined,
                })
              }
              disabled={cancelCampaignMutation.isPending}
            >
              {cancelCampaignMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Cancel Campaign
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
